import { Card } from "primereact/card"
import { Link, useParams } from "react-router-dom"
import { useEffect, useRef, useState } from "react"
import { connect, useDispatch, useSelector } from "react-redux"
import { AppDispatch } from "../../../../store"
import { matchJobToResumes, retrieveJob, updateResumeMatchingType } from "../JobAction"
import { Column } from "primereact/column"
import { DataTable, DataTableFilterMeta } from "primereact/datatable"
import LinkButton from "../../../../constants/helpers/LinkButton"
import { ProgressBar } from "primereact/progressbar"
import { Badge } from "primereact/badge"
import { Knob } from "primereact/knob"
import { Toast } from "primereact/toast"
import { FilterMatchMode } from "primereact/api"
import { Job, ResumeMatchingResult, ResumeMatchType } from "../interface"
import { Dropdown } from "primereact/dropdown"
import { SplitButton } from "primereact/splitbutton"
import { Toolbar } from "primereact/toolbar"
import { Button } from "primereact/button"
import { clear } from "console"

interface JobMatchingProps {
}

const JobMatching = (props: JobMatchingProps) => {
  const toast = useRef<any>(null)
  const dt = useRef<any>(null);
  const dispatch = useDispatch<AppDispatch>()

  const { jobId } = useParams<{ jobId: string }>()
  const { job, matchedResumes, totalRecords, refetchRequired } = useSelector((state: any) => state.job)

  // loading matching
  // const [loading, setLoading] = useState(true)

  useEffect(() => {
    // load job from db
    if (jobId) {
      dispatch(retrieveJob(Number(jobId)))
    }
  }, [])

  // datatable states
  let filters: DataTableFilterMeta = {
    fullName: { value: null, matchMode: FilterMatchMode.EQUALS },
    matchingPercentage: { value: null, matchMode: FilterMatchMode.EQUALS },
    description: { value: null, matchMode: FilterMatchMode.EQUALS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  }
  const [rows, setRows] = useState(5)
  const [selectedResumes, setSelectedResumes] = useState([] as ResumeMatchingResult[])
  const [selectedType, setSelectedType] = useState(null)
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: rows,
    page: 0,
    sortField: "matchingPercentage",
    sortOrder: -1,
    filters
  })

  const init = () => {
    dispatch(matchJobToResumes({jobId: Number(jobId), lazyParams}))
      .finally(() => {
        // setLoading(false)
      })
  }

  //Lazy loading
  useEffect(() => {
    init()
  }, [lazyParams])

  // Check for refetch and reload table every 5 seconds
  const refetchInterval = useRef<any>(null);
  useEffect(() => {
    console.log("Refetch required:", refetchRequired);
    if (refetchRequired)
      refetchInterval.current = setInterval(() => {
        init()
      }, 10000);
    else if (refetchInterval.current)
      clearInterval(refetchInterval.current);

    return () => clearInterval(refetchInterval.current);
  }, [refetchRequired]);

  const onPage = (event: any) => {
    setRows(event.rows)
    let _lazyParams = { ...lazyParams, ...event }
    setLazyParams(_lazyParams)
    setSelectedResumes([])
  }

  const onSort = (event: any) => {
    let _lazyParams = { ...lazyParams, ...event }
    setLazyParams(_lazyParams)
    setSelectedResumes([])
  }

  const onFilter = (event: any) => {
    let _lazyParams = { ...lazyParams, ...event }
    _lazyParams['first'] = 0;
    setLazyParams(_lazyParams)
    setSelectedResumes([])
  }
  
  const onTypeChange = (e: any) => {
    let _lazyParams = { ...lazyParams }
    _lazyParams.filters.type = { value: e.value, matchMode: FilterMatchMode.EQUALS }
    setLazyParams(_lazyParams)
    setSelectedType(e.value)
    setSelectedResumes([])
  }
  
  const handleResetFilters = () => {
    setSelectedType(null)
    dt.current.reset();
    let _lazyParams = { ...lazyParams }
    _lazyParams.filters = {}
    setLazyParams(_lazyParams)
  }

  const types = Object.keys(ResumeMatchType).filter(k => isNaN(Number(k)));
  const typeFilter = <Dropdown value={selectedType} options={types} onChange={onTypeChange} placeholder="All" className="p-column-filter" showClear></Dropdown>
  
  const getTypeStyling = (rowData: any) => {
    switch (rowData.type) {
      case ResumeMatchType.DEFAULT:
        return <Badge value={rowData.type}></Badge>
      case ResumeMatchType.INTERESTED:
        return <Badge value={rowData.type} severity="warning"></Badge>
      case ResumeMatchType.EMPLOYED:
        return <Badge value={rowData.type} severity="info"></Badge>
    }
  }

  const updateNewStatus = (e: any) => {
    if (selectedResumes.length === 0) {
      toast.current.show({ severity: 'warn', summary: 'No Resumes Selected', detail: 'Please select at least one resume to update status.' });
      return;
    }

    const updatedType = e.value;
    const resumeIds = selectedResumes.map((resume: ResumeMatchingResult) => resume.resumeId);

    dispatch(updateResumeMatchingType({ 
      jobId: Number(jobId), 
      resumeIds, 
      type: updatedType
    }))
      .then(() => {
        toast.current.show({ severity: 'success', summary: 'Matching Type Updated', detail: `Updated type to ${updatedType} for selected resumes.` });
        // selection and refresh table
        setSelectedResumes([]);
        init()
      })
      .catch((error: any) => {
        toast.current.show({ severity: 'error', summary: 'Error Updating Type', detail: error.message || 'An error occurred while updating type.' });
      });
  }

  const toolbar = () => {
    return (
      <>
        <Button type="button" label="Clear" className="p-button-outlined mr-2" icon="pi pi-filter-slash" onClick={handleResetFilters} />
        <Dropdown placeholder="Select new Type" options={types} onChange={updateNewStatus} disabled={selectedResumes.length === 0}></Dropdown>
      </>
    )
  }

  const refetchStatus = () => {
    if (refetchRequired)
      return <>
        <span className="pi pi-spinner pi-spin mr-2"></span> Refetching match results, table will refresh every 10 seconds
      </>;
  }

  return (
    <>
      <Toast ref={toast} />
      <Card title="Job Details" className="m-3"
        footer={
          <LinkButton label="View Job" icon="pi pi-eye" to={`/jobs/${jobId}`} />
        }>
        <div className="grid">
          <div className="col-12 md:col-6">
            <h5 className="m-0">Job Title</h5>
            <p className="m-0">{job.title}</p>
          </div>
          <div className="col-12 md:col-6">
            <h5 className="m-0">Job Description</h5>
            <p className="m-0">{job.description}</p>
          </div>
          <div className="col-12 md:col-6">
            <h5 className="m-0">Skills Required</h5>
            {job.skills && job.skills.map((skill: string, index: number) => (
              <span key={index} className="p-chip mr-1 mb-1">{skill}</span>
            ))}
          </div>
        </div>
      </Card>
      <Card title="Matched Resumes" className="m-3">
        <Toolbar className="p-mb-4" start={toolbar} end={refetchStatus}></Toolbar>
        <DataTable ref={dt} value={matchedResumes} first={lazyParams.first} rows={rows} totalRecords={totalRecords}
            onPage={onPage} onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder as any}
            onFilter={onFilter} filters={lazyParams.filters} filterDisplay='row'
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            rowsPerPageOptions={[5, 10, 25]} currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            selection={selectedResumes} onSelectionChange={e => {
              const resumes = e.value as ResumeMatchingResult[]
              setSelectedResumes(resumes)
            }}
            lazy paginator stripedRows>
          <Column selectionMode="multiple" style={{ width: '3em', flexGrow: 0 }}></Column>
          <Column field="resumeId" header="Resume ID" style={{width: '5%'}} body={(rowData) => (
            <Link to={`/resumes/${rowData.resumeId}`}>{rowData.resumeId}</Link>
          )} />
          <Column field="fullName" header="Full Name" style={{width: '10%'}} filter sortable></Column>
          <Column field="matchingPercentage" header="Matching Percentage" style={{width: '8%'}} body={rowData => (
            <Knob value={rowData.matchingPercentage} valueTemplate={'{value}%'} readOnly />
          )} filter sortable></Column>
          <Column field="matchedSkills" header="Matched Skills" style={{width: '12%'}} body={rowData => (
            <div className="flex flex-wrap">
              {rowData.matchedSkills.map((skill: string, index: number) => (
                <span key={index} className="p-chip mr-1 mb-1">{skill}</span>
              ))}
            </div>
          )}></Column>
          <Column field="matchedWorkExperience" header="Matched Work Experience" style={{width: '20%'}} body={rowData => (
            <div className="fluid">
              {rowData.matchedWorkExperience.map((experience: any, index: number) => (
                <div key={index} className="grid">
                  <div className="col-12">
                    <small><b>{experience.companyName}</b> - {experience.title} ({experience.numberOfYears} years)</small>
                    {/* <p className="m-0">{experience.roleSummary.substring(0, 100)}...</p> */}
                  </div>
                </div>
              ))}
            </div>
          )}></Column>
          <Column field="detailedAnalysis" header="Analysis" style={{width: '30%'}} filter sortable></Column>
          <Column field="type" header="Type" style={{width: '5%'}} filter sortable body={getTypeStyling} filterElement={typeFilter}></Column>
        </DataTable>
      </Card>
    </>
  )
}

export default JobMatching